import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Alert } from 'app/models/dto/alert.model';
import { Application } from 'app/models/dto/deal-application.model';
import { Deal } from 'app/models/dto/deal.model';
import { ApplicationService } from 'app/services/application/application.service';
import { LenderApiService } from 'app/services/lender-api/lender-api.service';
import { throwError, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CreateNewDealAlert } from '../deal-alerts/deal-alerts.actions';
import { CreateAPIApplication, CreateApplication, GetDealApplications, ClearApplicationStore, GetBorrowerApplications } from './application.actions';
import * as _ from 'lodash';
import { Injectable } from '@angular/core';

export class ApplicationStateModel {
  applications: Application[];
}

@State<ApplicationStateModel>({
	name: 'applications',
	defaults: {
    applications: []
	}
})

@Injectable()

export class ApplicationState {

  @Selector()
  static allDealApplications(state: ApplicationStateModel) {
    return state.applications.filter( a => !!a.dealId);
  }

  @Selector()
  static dealApplications(state: ApplicationStateModel) {
    return (dealId: number) => state.applications.filter(a => a.dealId === dealId);
  }

  @Selector()
  static lastDealApplication(state: ApplicationStateModel) {
    return (dealId: number) => {
      const apps = state.applications.filter(a => a.dealId === dealId);
      return apps.length ? apps[apps.length - 1] : null;
    };
  }

  constructor(
    private applicationService: ApplicationService,
    private lenderApiService: LenderApiService,
    private store: Store
  ) {}

	@Action(GetDealApplications)
	getDealApplications({ getState, patchState }: StateContext<ApplicationStateModel>, { dealId, refresh = false }: GetDealApplications) {
    const appsAlreadyInState = getState().applications.filter(a => a.dealId === dealId).length;
    if (!appsAlreadyInState || refresh) {
      return this.applicationService.getDealApplications(dealId).pipe(tap(applications => {
        patchState({ applications: [ ...getState().applications.filter(a => a.dealId !== dealId), ...applications ] });
      }));
    }
  }

	@Action(GetBorrowerApplications)
	getBorrowerApplications({ getState, patchState }: StateContext<ApplicationStateModel>, { borrowerId }: GetBorrowerApplications) {
    return this.applicationService.getBorrowerApplications(borrowerId).pipe(
      catchError(() => of()),
      tap(applications => {
        const ids = applications.map(a => a.id);
        patchState({ applications: [ ...getState().applications.filter(a => !ids.includes(a.id)), ...applications ] });
      }));
  }

	@Action(CreateApplication)
	createApplication({ getState, patchState }: StateContext<ApplicationStateModel>, { deal }: CreateApplication) {
    const newPackage = {
      dealId: deal.id,
      lenderUserId: deal.lenderUserId,
      loanProductId: deal.loanProductId,
      contactLenderUserId: deal.loan_product ? deal.loan_product.contactLenderUserId : null,
      inactive: null,
      message: null
    };
    return this.applicationService.post(newPackage).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error sending this package to the lender. Please try again.'
      };
      this.store.dispatch(new CreateNewDealAlert(deal.id, newAlert));
      return of();
    }), tap(application => {
      patchState({ applications: [ application, ...getState().applications ] });
      const newAlert: Alert = {
        level: 'success',
        message: 'Application successfully submitted',
        dismissDelay: 5000
      };
      this.store.dispatch(new CreateNewDealAlert(newPackage.dealId, newAlert));
    }));
  }

	@Action(CreateAPIApplication)
	createAPIApplication({}: StateContext<ApplicationStateModel>, { deal }: CreateAPIApplication) {
    return this.lenderApiService.postApplication(deal).pipe(
      catchError(err => {
        const newAlert: Alert = {
          level: 'error',
          message: this.apiErrorMessage(err, deal)
        };
        this.store.dispatch(new CreateNewDealAlert(deal.id, newAlert));
        this.store.dispatch(new GetDealApplications(deal.id));
        return of();
      }),
      tap(response => {
        this.handleApiSuccess(deal, response);
        this.store.dispatch(new GetDealApplications(deal.id));
      })
    );
  }

  @Action(ClearApplicationStore)
  clearApplicationStore({ patchState }: StateContext<ApplicationStateModel>, {}: ClearApplicationStore) {
    patchState({ applications: [] });
  }

  private handleApiSuccess(deal: Deal, lenderResponse: any) {
    // show that api post to lender was successful
    const newAlert: Alert = {
      level: 'success',
      message: 'Application successfully submitted',
      dismissDelay: 5000
    };
    this.store.dispatch(new CreateNewDealAlert(deal.id, newAlert));

    // handle lender/response specific warning messages, if applicable
    // NOTE: the code below has been blindly migrated since there is no way to test these error messages right now
    // at the time of this migration, the only api enabled lender subbing deals is on deck and the below code doesn't apply to them
    if ((lenderResponse.approved === 1 || !lenderResponse.hasOwnProperty('approved')) && deal.supportedApiActions) {
      if (deal.supportedApiActions.sentEmail) {
        const warning: Alert = {
          level: 'warning',
          message: 'An email has been sent to the borrower to complete application at Lender portal',
          dismissDelay: 5000
        };
        return this.store.dispatch(new CreateNewDealAlert(deal.id, warning));
      }
      if (deal.supportedApiActions.completeAtLender) {
        const warning: Alert = {
          level: 'warning',
          message: 'Borrower needs to complete application at Lender portal',
          dismissDelay: 5000
        };
        return this.store.dispatch(new CreateNewDealAlert(deal.id, warning));
      } else {
        if (!deal.supportedApiActions.documents && !deal.supportedApiActions.hideDocumentWarning) {
          const warning: Alert = {
            level: 'warning',
            message: 'Documents need to be emailed manually',
            dismissDelay: 5000
          };
          return this.store.dispatch(new CreateNewDealAlert(deal.id, warning));
        }
      }
    }
  }

  private apiErrorMessage(err: any, deal: Deal) {
    if (err.data) {
      err = err.data;
    }
    if (err.errors) {
      const log = deal.supportedApiActions && deal.supportedApiActions.skipErrorFormatting
        ? err.errors
        : _.map(err.errors, this.formatErrorMessage);
      return log.join('<br/>');
    } else if (err.data && err.data.error && err.data.error.message) {
      return err.data.error.message;
    } else {
      return 'There was a problem - Please ask your team lead for assistance';
    }
  }

  private formatErrorMessage(error: any) {
    if (error && error.message) {
      return error.message;
    }
    if ((typeof error !== 'string' && !(error instanceof String)) || !error.length) {  // Must be a string with at least 1 character
      return '';
    }
    error = error.charAt(0).toUpperCase() + error.substring(1); // Capitalize first letter
    return error
      .replace(/\[\d+\]/g, '') // Remove array indexes
      .replace(/\.+/g, ' ') // Remove . separator
      .replace(/may not be null/gi, 'is invalid')
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camel case
      .replace(/\^*.([A-Z])([a-z])/g, function (str: string) {
        return str.toLowerCase();
      });
  }
}
